import { Button, Card } from 'react-bootstrap';
import SimpleTableFilterDropdown from "components/global/SimpleTableFilterDropdown";
import LargeCheckBox from "components/global/Constant/LargeCheckBox";
import SimpleTable from "components/global/SimpleTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory } from "react-router-dom";
import {
  handleBusinessDashboardSortChange,
  handleRemoveSavedOpportunityFromDashboard,
  setBusinessDashboardAppliedFilters,
  setBusinessDashboardDropdownFilter,
  showProgramsOfferedModal,
  resetBusinessDashboardFilterInputs
} from "../BusinessDashboard.helpers";
import businessDashboardSignal from "signals/BusinessDashboard.signal";
import { uniqById } from "utils/array";
import $appSettings from 'signals/AppSettings.signal';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const BusinessSavedOpportunities = () => {
  const { PROVIDER_TYPES, PORTAL_TYPES } = $appSettings.value.constants;
  const history = useHistory();
  const {
    favoriteRecords,
    dropdownFilters,
    sort,
  } = businessDashboardSignal.value;

  const lenders = favoriteRecords
    .filter((fb) => !!fb.lender)
    .map((fb) => fb.lender);
  const ventureCapitalFirms = favoriteRecords
    .filter((fb) => !!fb.ventureCapitalFirm)
    .map((fb) => fb.ventureCapitalFirm);

  const uniqLenders = uniqById(lenders);
  const uniqVcfs = uniqById(ventureCapitalFirms);

  return (
    <Card className="p-24 bg-light">
      <>
        <h4 className="mb-32">Saved opportunities</h4>
        <div className="mb-16">
        <SimpleTableFilterDropdown
          filtersState={dropdownFilters}
          onChange={({ name, value }) =>
            setBusinessDashboardDropdownFilter({ name, value })
          }
          onApply={(filters) =>
            setBusinessDashboardAppliedFilters(filters)
          }
          onClearFilterInputs={resetBusinessDashboardFilterInputs}
          filters={[
            {
              type: 'integer',
              label: 'ID',
              key: 'id',
            },
            {
              type: 'select',
              label: 'Organization Name',
              key: 'lenderName',
              options: uniqLenders
                .map((lender) => ({
                  value: `${lender.id}-lender`,
                  label: lender.name,
                }))
                .concat(
                  uniqVcfs.map((vcf) => ({
                    value: `${vcf.id}-vc`,
                    label: vcf.name,
                  }))
                ),
            },
            {
              type: 'select',
              label: 'Lender/VC',
              key: 'organizationType',
              options: [
                {
                  label: 'Lender',
                  value: PORTAL_TYPES.lender,
                },
                { label: 'VC', value: PORTAL_TYPES.vc },
              ],
            },
            {
              type: 'select',
              label: 'Lender Type',
              key: 'lenderType',
              options: Object.values(PROVIDER_TYPES).map(
                (lenderType) => ({
                  value: lenderType.value,
                  label: lenderType.annualReportLabel,
                })
              ),
            },
            {
              type: 'select',
              label: 'Requires Business Plan',
              key: 'requiresBusinessPlan',
              options: [
                {
                  value: 'true',
                  label: 'Yes',
                },
                {
                  value: 'false',
                  label: 'No',
                },
              ],
            },
            {
              type: 'select',
              label: 'Funds Startups',
              key: 'fundsStartups',
              options: [
                {
                  value: 'true',
                  label: 'Yes',
                },
                {
                  value: 'false',
                  label: 'No',
                },
              ],
            },
          ]}
        />
        </div>
        <SimpleTable
          headers={[
            {
              key: 'id',
              title: 'ID',
              sortable: true,
            },
            {
              key: 'lenderName',
              title: 'Organization Name',
              sortable: true,
            },
            {
              key: 'organizationType',
              title: 'Lender/VC',
            },
            {
              key: 'lenderType',
              title: 'Lender Type',
              sortable: true,
            },
            {
              key: 'requiresBusinessPlan',
              title: 'Requires Business Plan',
              sortable: true,
            },
            {
              key: 'fundsStartups',
              title: 'Funds Startups',
              sortable: true,
            },
            {
              key: 'programName',
              title: 'Programs',
            },
            {
              key: 'action',
              title: 'Action',
            },
          ]}
          rows={favoriteRecords.map((favoriteRecord) => ({
            id: favoriteRecord.id,
            lenderName:
              favoriteRecord.lender?.name ||
              favoriteRecord.ventureCapitalFirm?.name,
            organizationType: favoriteRecord.lender ? 'Lender' : 'VC',
            lenderType:
              Object.values(PROVIDER_TYPES).find(
                (lenderType) =>
                  (favoriteRecord.lender?.lenderType ||
                    favoriteRecord.ventureCapitalFirm?.lenderType) ===
                  lenderType.value
              )?.annualReportLabel || '',
            requiresBusinessPlan: ({ key }) => (
              <td key={key}>
                {favoriteRecord.lender?.requiresBusinessPlan ||
                favoriteRecord.ventureCapitalFirm
                  ?.requiresBusinessPlan ? (
                  <LargeCheckBox
                    checked
                    disabled
                    size="sm"
                    centeringClass="text-left"
                  />
                ) : (
                  <FontAwesomeIcon
                    className="text-muted ml-8"
                    icon={faTimes}
                  />
                )}
              </td>
            ),
            fundsStartups: ({ key }) => (
              <td key={key}>
                {favoriteRecord.lender?.fundsStartups ||
                favoriteRecord.ventureCapitalFirm?.fundsStartups ? (
                  <LargeCheckBox
                    checked
                    disabled
                    size="sm"
                    centeringClass="text-left"
                  />
                ) : (
                  <FontAwesomeIcon
                    className="text-muted ml-8"
                    icon={faTimes}
                  />
                )}
              </td>
            ),
            programName: ({ key }) => (
              <td key={key}>
                <Button
                  size="sm"
                  className="rounded"
                  onClick={() =>
                    showProgramsOfferedModal(
                      favoriteRecord.lender ||
                        favoriteRecord.ventureCapitalFirm
                    )
                  }
                >
                  View
                </Button>
              </td>
            ),
            action: ({ key }) => (
              <td key={key} align="right">
                <div className="d-flex flex-row justify-content-end">
                  <Button
                    size="sm"
                    onClick={() =>
                      handleRemoveSavedOpportunityFromDashboard(
                        favoriteRecord.id
                      )
                    }
                    className="rounded mr-8"
                    variant="outline-primary"
                  >
                    Unsave
                  </Button>
                  <Button
                    variant="outline-dark"
                    size="sm"
                    className="rounded"
                    onClick={() =>
                      history.push(
                        `${history.location.pathname}/${
                          favoriteRecord.lender ? 'lenders' : 'vc'
                        }/${
                          favoriteRecord.lender
                            ? favoriteRecord.lenderId
                            : favoriteRecord.ventureCapitalFirmId
                        }`
                      )
                    }
                  >
                    Details
                  </Button>
                </div>
              </td>
            ),
          }))}
          onSort={(sort) => handleBusinessDashboardSortChange(sort)}
          sort={sort}
        />
      </>
      {/*<ApplicationsList />*/}
    </Card>
  )
}

export default BusinessSavedOpportunities;
